import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
  AlertCircle, BookOpen, Calendar, Clock, Flame, Heart,
  Moon, Music, Sparkles, Star, Sun, Sunrise, Sunset,
} from 'lucide-react';

/**
 * תבנית מלכותית — רקע כחול כהה עם זהב בוהק.
 * עשירה ומכובדת, מתאימה לתצוגות ייצוגיות של בית כנסת.
 */

const NAVY = '#0d1b2a';
const NAVY_LIGHT = '#1b2d44';
const GOLD = '#C9A227';
const GOLD_DIM = 'rgba(201, 162, 39, 0.35)';
const IVORY = '#F5F0E8';
const MUTED = 'rgba(245, 240, 232, 0.5)';

const CATEGORY_ICONS = {
  tachanun: AlertCircle, hallel: Music, prayerAdditions: BookOpen,
  seasonalPrayers: Sunrise, seasonalTransitions: Sun, selichot: Heart,
  weekdayPrayers: Clock, shabbatAdditions: Flame, motzaeiShabbat: Moon,
  seasonalCustoms: Star, liturgy: Sparkles, leyning: BookOpen,
  haftarah: BookOpen, mourningPeriods: Sunset, general: Calendar,
};

const CATEGORY_LABELS = {
  tachanun: 'תחנון', hallel: 'הלל', prayerAdditions: 'תוספות תפילה',
  seasonalPrayers: 'תפילות עונתיות', seasonalTransitions: 'מעברים עונתיים',
  selichot: 'סליחות', weekdayPrayers: 'תפילות ימי חול',
  shabbatAdditions: 'תוספות שבת', motzaeiShabbat: 'מוצאי שבת',
  seasonalCustoms: 'מנהגים עונתיים', liturgy: 'פיוטים וקריאות',
  leyning: 'קריאת התורה', haftarah: 'הפטרה',
  mourningPeriods: 'ימי אבל ותענית', general: 'כללי',
};

// -- קו מפריד זהב מעודן -------------------------------------------------------
const GoldDivider = () => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    my: 0.75,
    width: '80%',
    mx: 'auto',
  }}>
    <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, transparent, ${GOLD_DIM})` }} />
    <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: GOLD, opacity: 0.5 }} />
    <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, ${GOLD_DIM}, transparent)` }} />
  </Box>
);

// -- כותרת קטגוריה -------------------------------------------------------------
const CategoryGroup = ({ category, items }) => {
  const label = CATEGORY_LABELS[category] || category;
  const IconComp = CATEGORY_ICONS[category] || Calendar;

  return (
    <Box sx={{ mb: 1 }}>
      {/* כותרת קטגוריה */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        mb: 0.4,
        px: 0.75,
      }}>
        <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, transparent, ${GOLD_DIM})` }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconComp size={10} style={{ color: GOLD, opacity: 0.5 }} />
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.55rem',
            color: GOLD,
            letterSpacing: '0.1em',
            whiteSpace: 'nowrap',
            fontWeight: 600,
            opacity: 0.65,
          }}>
            {label}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, ${GOLD_DIM}, transparent)` }} />
      </Box>

      {/* פריטים */}
      {items.map((item, i) => (
        <ItemRow key={item.id || `${category}-${i}`} item={item} />
      ))}
    </Box>
  );
};

// -- פריט בודד -----------------------------------------------------------------
const ItemRow = ({ item }) => {
  const IconComp = CATEGORY_ICONS[item.category] || Calendar;
  const isHigh = item.priority === 'high';

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1,
      py: 0.6,
      px: 1.25,
      borderRadius: '6px',
      borderRight: isHigh ? `3px solid ${GOLD}` : '3px solid transparent',
      background: isHigh ? `rgba(201, 162, 39, 0.08)` : 'transparent',
      transition: 'background 0.2s',
    }}>
      <Box sx={{
        mt: '3px',
        flexShrink: 0,
        color: isHigh ? GOLD : MUTED,
        display: 'flex',
        alignItems: 'center',
      }}>
        <IconComp size={13} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.78rem',
            fontWeight: isHigh ? 700 : 600,
            color: isHigh ? GOLD : IVORY,
            lineHeight: 1.4,
          }}>
            {item.label}
          </Typography>
          {item.value && (
            <Typography sx={{
              fontFamily: 'Frank Ruhl Libre, David, serif',
              fontSize: '0.73rem',
              color: isHigh ? IVORY : MUTED,
              lineHeight: 1.4,
            }}>
              {item.value}
            </Typography>
          )}
        </Box>
        {item.detail && (
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.62rem',
            color: MUTED,
            lineHeight: 1.5,
            mt: 0.1,
          }}>
            {item.detail}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// -- עזר: קיבוץ לפי קטגוריה ---------------------------------------------------
function groupByCategory(items) {
  const map = {};
  items.forEach((item) => {
    const cat = item.category || 'general';
    if (!map[cat]) map[cat] = [];
    map[cat].push(item);
  });
  return Object.entries(map);
}

// -- יום lookahead בודד --------------------------------------------------------
const LookaheadDay = ({ dayData }) => (
  <Box sx={{ mt: 1.5 }}>
    <GoldDivider />
    <Typography sx={{
      fontFamily: 'Frank Ruhl Libre, David, serif',
      fontSize: '0.74rem',
      fontWeight: 700,
      color: GOLD,
      textAlign: 'center',
      mb: 0.5,
      opacity: 0.75,
      letterSpacing: '0.03em',
    }}>
      {dayData.hebrewDate?.hebrew || dayData.date}
    </Typography>
    {dayData.items.length === 0 ? (
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, David, serif',
        fontSize: '0.66rem',
        color: MUTED,
        textAlign: 'center',
      }}>
        אין ענייני יום מיוחדים
      </Typography>
    ) : (
      groupByCategory(dayData.items).map(([cat, catItems]) => (
        <CategoryGroup key={cat} category={cat} items={catItems} />
      ))
    )}
  </Box>
);

// == תבנית מלכותית =============================================================
const Royal = ({
  today,
  items,
  lookaheadItems,
  lookaheadDays,
  loading,
  error,
  title,
  showTitle,
}) => {
  const hebrewDateStr = today?.hebrewDate?.hebrew || '';
  const grouped = groupByCategory(items || []);

  return (
    <Box sx={{
      background: `linear-gradient(180deg, ${NAVY_LIGHT} 0%, ${NAVY} 100%)`,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      direction: 'rtl',
      overflowY: 'auto',
      padding: '16px 14px',
      position: 'relative',
      // מסגרת זהב
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 5,
        border: `1.5px solid ${GOLD}`,
        borderRadius: '4px',
        pointerEvents: 'none',
        opacity: 0.35,
      },
      '&::-webkit-scrollbar': { width: 3 },
      '&::-webkit-scrollbar-thumb': { background: `${GOLD}40`, borderRadius: 2 },
    }}>
      {/* כותרת */}
      {showTitle && (
        <Box sx={{
          textAlign: 'center',
          position: 'relative',
          zIndex: 1,
          mb: 0.25,
        }}>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.68rem',
            color: GOLD,
            letterSpacing: '0.18em',
            fontWeight: 700,
          }}>
            {title}
          </Typography>
        </Box>
      )}

      {/* תאריך עברי */}
      {hebrewDateStr && (
        <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <GoldDivider />
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.95rem',
            fontWeight: 700,
            color: IVORY,
            letterSpacing: '0.04em',
            textShadow: `0 0 20px rgba(201,162,39,0.2)`,
          }}>
            {hebrewDateStr}
          </Typography>
          <GoldDivider />
        </Box>
      )}

      {/* loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1, position: 'relative', zIndex: 1 }}>
          <CircularProgress size={18} sx={{ color: GOLD }} />
        </Box>
      )}

      {/* error */}
      {error && (
        <Typography sx={{
          fontSize: '0.68rem',
          color: '#e57373',
          textAlign: 'center',
          mb: 0.5,
          position: 'relative',
          zIndex: 1,
        }}>
          שגיאה בטעינה
        </Typography>
      )}

      {/* ריק */}
      {grouped.length === 0 && !loading && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, David, serif',
          fontSize: '0.75rem',
          color: MUTED,
          textAlign: 'center',
          mt: 2,
          position: 'relative',
          zIndex: 1,
        }}>
          אין ענייני יום מיוחדים להיום
        </Typography>
      )}

      {/* פריטי היום */}
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {grouped.map(([cat, catItems]) => (
          <CategoryGroup key={cat} category={cat} items={catItems} />
        ))}
      </Box>

      {/* ימי lookahead */}
      {lookaheadDays > 0 && lookaheadItems && lookaheadItems.length > 0 && (
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {lookaheadItems.map((dayData, i) => (
            <LookaheadDay key={dayData.date || i} dayData={dayData} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Royal;
